/**
 * Name Display Privacy Utility
 *
 * Controls whether to show real name or nickname based on:
 * - Context (social vs club_official)
 * - Relationship (friend vs non-friend)
 * - User's privacy settings
 *
 * Rules:
 * - Club official contexts (tournaments, events, admin, coaching) → Always real name
 * - Social contexts (search, directory, profile, posts, notifications, messaging, tee sheet):
 *   - If show_real_name = true (default) → Real name
 *   - If show_real_name = false:
 *     - Friends → Real name
 *     - Non-friends → Nickname (display_name)
 */

export type NameContext = 'social' | 'club_official';

export interface UserNameData {
  first_name: string;
  last_name: string;
  display_name?: string | null;
  show_real_name?: boolean;
}

/**
 * Get the appropriate display name for a user based on context and relationship
 *
 * @param user - User data with name fields
 * @param isFriend - Whether the viewer is a friend of this user
 * @param context - 'social' or 'club_official'
 * @returns The name to display
 */
export function getDisplayName(
  user: UserNameData,
  isFriend: boolean,
  context: NameContext = 'social'
): string {
  const realName = formatRealName(user.first_name, user.last_name);
  const nickname = user.display_name?.trim() || realName;

  // Club official contexts always show real name
  if (context === 'club_official') {
    return realName;
  }

  // Default behavior: show real name (show_real_name defaults to true)
  const showRealName = user.show_real_name !== false;

  if (showRealName) {
    return realName;
  }

  // User wants to hide real name from non-friends
  if (isFriend) {
    return realName; // Friends always see real name
  }

  return nickname; // Non-friends see nickname
}

/**
 * Format first and last name into a full name
 */
export function formatRealName(firstName: string, lastName: string): string {
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';

  if (first && last) {
    return `${first} ${last}`;
  }

  return first || last || 'Unknown';
}

/**
 * Check if nickname is set and different from real name
 */
export function hasCustomNickname(user: UserNameData): boolean {
  if (!user.display_name?.trim()) {
    return false;
  }

  const realName = formatRealName(user.first_name, user.last_name);
  return user.display_name.trim().toLowerCase() !== realName.toLowerCase();
}

/**
 * Get initials from a name (for avatars)
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}
